import { useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useI18n } from '../i18n';
import { itemPorId, trackAddToWishlist } from '../lib/tracking';

interface Props {
  templateId: number;
  name?: string;
}

/**
 * Estrela de favorito ao lado do nome da skin.
 *
 * Substitui o botão "Acompanhar" da barra: favoritar é uma ação de um clique,
 * não merece um botão do tamanho dos filtros. O estado inicial vem da lista de
 * favoritos, para a estrela já nascer preenchida no que você segue.
 */
export function FavoriteStar({ templateId, name }: Props) {
  const { lang } = useI18n();
  const [favorito, setFavorito] = useState<boolean | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    void api
      .watchlist()
      .then((r) => {
        if (vivo) setFavorito(r.items.some((i) => i.templateId === templateId));
      })
      .catch(() => {
        // Sem watchlist disponível a estrela aparece vazia e ainda dá para clicar.
        if (vivo) setFavorito(false);
      });
    return () => {
      vivo = false;
    };
  }, [templateId]);

  async function alternar() {
    setOcupado(true);
    setErro(null);
    const proximo = !favorito;
    try {
      if (proximo) await api.addToWatchlist({ templateId, label: name });
      else await api.removeFromWatchlist(templateId);
      // Só depois de a API confirmar: favorito que falhou não é favorito.
      if (proximo) trackAddToWishlist(itemPorId(templateId, name));
      setFavorito(proximo);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui salvar o favorito.');
    } finally {
      setOcupado(false);
    }
  }

  const pt = lang === 'pt';
  const titulo = erro
    ? erro
    : favorito
      ? pt
        ? 'Remover dos favoritos'
        : 'Remove from favorites'
      : pt
        ? 'Adicionar aos favoritos'
        : 'Add to favorites';

  return (
    <button
      className={`fav-star ${favorito ? 'on' : ''} ${erro ? 'erro' : ''}`}
      onClick={() => void alternar()}
      disabled={ocupado || favorito === null}
      title={titulo}
      aria-label={titulo}
      aria-pressed={favorito ?? false}
    >
      {favorito ? '★' : '☆'}
    </button>
  );
}
